import type { AnalyzerMetaInterface } from '~/store/analyzer/list'

export type AnalyzerTemplateContext = {
  provider: string
  owner: string
  repo: string
  vcsHost?: string | null
  forTemplate?: boolean
}

type AnalyzerMetaProperty = AnalyzerMetaInterface['properties'][string]

const VCS_HOST_MAP: Record<string, string> = {
  gh: 'github.com',
  gl: 'gitlab.com',
  bb: 'bitbucket.com'
}

export const parseAnalyzerTemplate = (
  candidate: string,
  context: AnalyzerTemplateContext
): string => {
  const { provider, owner, repo, vcsHost, forTemplate } = context
  const host = vcsHost || VCS_HOST_MAP[provider] || ''
  const repoName = forTemplate ? '<repository-name>' : repo

  return candidate
    .replaceAll('<%=  vcs_host %>', host)
    .replaceAll('<%=  login %>', owner)
    .replaceAll('<%=  name %>', repoName)
}

export const applyTemplateToValue = <T>(value: T, context: AnalyzerTemplateContext): T => {
  if (typeof value === 'string') {
    return parseAnalyzerTemplate(value, context) as T
  }

  if (Array.isArray(value)) {
    return value.map((item) =>
      typeof item === 'string' ? parseAnalyzerTemplate(item, context) : item
    ) as T
  }

  return value
}

export const getAnalyzerPropertyDefault = (
  property: AnalyzerMetaProperty,
  context: AnalyzerTemplateContext
): AnalyzerMetaProperty['default'] | undefined => {
  const defaultValue = property.default
  if (defaultValue === undefined) {
    return undefined
  }

  if (property.type === 'boolean') {
    return String(defaultValue).toLowerCase() === 'true'
  }

  if (property.type === 'array') {
    if (Array.isArray(defaultValue)) {
      const stringValues = defaultValue.map((item) =>
        typeof item === 'string' ? parseAnalyzerTemplate(item, context) : String(item)
      )
      if (property.items?.type !== 'integer') {
        return stringValues
      }

      const numericValues = stringValues.map((item) => Number(item))
      const hasInvalidNumber = numericValues.some((item) => Number.isNaN(item))

      return hasInvalidNumber ? stringValues : numericValues
    }
    if (typeof defaultValue === 'string') {
      return [parseAnalyzerTemplate(defaultValue, context)]
    }
    return defaultValue
  }

  if (property.type === 'string') {
    if (typeof defaultValue === 'string') {
      return parseAnalyzerTemplate(defaultValue, context)
    }
    if (Array.isArray(defaultValue)) {
      if (defaultValue.length > 0) {
        const first = defaultValue[0]
        return typeof first === 'string' ? parseAnalyzerTemplate(first, context) : first
      }
      return undefined
    }
    return defaultValue
  }

  return defaultValue
}

export const buildDefaultAnalyzerMeta = (
  analyzerMeta: AnalyzerMetaInterface,
  context: AnalyzerTemplateContext
): Record<string, string | number | boolean | string[] | number[]> => {
  const requiredProps = analyzerMeta.required || []
  const optionalRequiredProps = analyzerMeta.optional_required || []
  const primaryProps = [...new Set([...requiredProps, ...optionalRequiredProps])]
  const properties = analyzerMeta.properties || {}
  const meta: Record<string, string | number | boolean | string[] | number[]> = Object.create(null)

  primaryProps.forEach((name) => {
    if (!(name in properties)) {
      return
    }
    const prop = properties[name]
    const defaultValue = getAnalyzerPropertyDefault(prop, context)
    if (defaultValue === undefined || defaultValue === '') {
      return
    }
    meta[name] = defaultValue as string | number | boolean | string[] | number[]
  })

  return meta
}

export const buildAnalyzerMetaDefaultsForProps = (
  analyzerMeta: AnalyzerMetaInterface,
  context: AnalyzerTemplateContext,
  propNames: string[],
  useDefaults = true
): Record<string, string | number | boolean | string[] | number[]> => {
  if (!useDefaults) {
    return Object.create(null) as Record<string, string | number | boolean | string[] | number[]>
  }
  const properties = analyzerMeta.properties || {}
  const meta: Record<string, string | number | boolean | string[] | number[]> = Object.create(null)
  propNames.forEach((name) => {
    if (!(name in properties)) return
    const prop = properties[name]
    const defaultValue = getAnalyzerPropertyDefault(prop, context)
    if (defaultValue === undefined || defaultValue === '') return
    meta[name] = defaultValue as string | number | boolean | string[] | number[]
  })
  return meta
}
